// @flow
import * as React from 'react';
import styled from 'styled-components';
import { width, maxWidth, flex } from 'styled-system';
import { getJson } from 'utils/http';
import { imageUrl, imageSizeUrl } from 'utils/filestack';
import { RichTextHeadline, H3, Body, BodySmall } from 'components/Text';
import Box from 'components/Box';
import type { CtaPromoBlockData } from 'api/cms';
import {
  TrackedPrimaryButtonLink,
  TrackedSecondaryButtonLink
} from 'components/Button';
import { FilestackImage } from 'components/Image';
import { colors, gutterWidth } from 'theme';

const Figcaption = styled.figcaption`
  ${width} ${maxWidth} ${flex}
`;

const Image = styled(({ image, className, ...props }) => (
  <Box className={className}>
    <FilestackImage data={image} {...props} />
  </Box>
))`
  width: 100%;
  height: 100%;
  background-image: url(${props =>
    imageUrl({
      handle: props.image.handle,
      mimetype: props.image.mimetype,
      transform: `resize=${
        props.width
          ? `w:${props.width}`
          : props.height
          ? `h:${props.height}`
          : ''
      },fit:max/output=format:jpg/quality=value:80`
    })});
  background-size: cover;
  background-position: center;
  * {
    height: 100%;
  }
  svg {
    width: 40px;
  }
  img {
    display: block;
    visibility: hidden;
    height: 100%;
    width: 100%;
  }
  @supports (object-fit: cover) {
    background: none;
    img {
      visibility: visible;
      object-fit: cover;
    }
  }
`;

const BodyText = styled(({ isSidebar, ...props }) =>
  isSidebar ? <BodySmall {...props} /> : <Body {...props} />
)`
  *:last-child {
    margin-bottom: 0;
  }
`;

const CtaPromoBlock = ({
  data: {
    headline,
    text,
    image,
    primaryButtonText,
    primaryButtonUrl,
    secondaryButtonText,
    secondaryButtonUrl
  },
  isSidebar,
  blockIndex,
  pageUpdated
}: {
  data: CtaPromoBlockData,
  isSidebar?: boolean,
  blockIndex: number,
  pageUpdated: string
}) => {
  const { useEffect, useState, useRef } = React;
  const [contentHeight, setContentHeight] = useState(0);
  const [imageDimensions, setImageDimensions] = useState(null);
  const contentBox = useRef(null);
  const Headline = isSidebar ? H3 : RichTextHeadline;

  useEffect(
    () => {
      const height = contentBox.current ? contentBox.current.offsetHeight : 0;
      if (height !== contentHeight) {
        setContentHeight(height);
        getJson(imageSizeUrl({ handle: image.handle })).then(res => {
          const imageRatio = res.width / res.height;
          const containerRatio = 672 / height;
          setImageDimensions(
            imageRatio >= containerRatio ? { height } : { width: 672 }
          );
        });
      }
    },
    [
      headline,
      text,
      primaryButtonText,
      secondaryButtonText,
      contentBox,
      contentHeight,
      image
    ]
  );

  return (
    <Box
      display="flex"
      width="100%"
      flexDirection={['column-reverse', null, null, isSidebar ? null : 'row']}
      m="0"
      as="figure"
      bg={colors.offWhite}
    >
      <Figcaption
        width={['100%', null, null, isSidebar ? null : '50%']}
        maxWidth={['none', null, null, isSidebar ? null : 480]}
        flex="0 0 auto"
        ref={contentBox}
      >
        <Box
          display="flex"
          p={[gutterWidth / 2, null, null, isSidebar ? null : gutterWidth]}
          alignItems="center"
          bg={colors.offWhite}
        >
          <Box width="100%">
            <Headline mb={8}>{headline}</Headline>
            <BodyText
              analyticsData={{
                eventName: 'CMS Block',
                eventProperties: {
                  block_position: blockIndex,
                  block_type: 'CTA Promo'
                }
              }}
              isSidebar={isSidebar}
              dangerouslySetInnerHTML={{ __html: text }}
            />
            {((primaryButtonText && primaryButtonUrl) ||
              (secondaryButtonText && secondaryButtonUrl)) && (
              <Box
                display="flex"
                mt={['22px', null, '32px']}
                mx="-6px"
                flexWrap="wrap"
                width="100%"
              >
                {primaryButtonText && primaryButtonUrl && (
                  <Box m="6px">
                    <TrackedPrimaryButtonLink
                      color={colors.offWhite}
                      size="small"
                      href={primaryButtonUrl}
                      eventName="CMS Block"
                      eventProperties={{
                        block_position: blockIndex,
                        block_type: 'Call to Action Promo',
                        cta_type: 'Button',
                        button_text: primaryButtonText,
                        button_number: 1,
                        page_updated: pageUpdated
                      }}
                    >
                      {primaryButtonText}
                    </TrackedPrimaryButtonLink>
                  </Box>
                )}
                {secondaryButtonText && secondaryButtonUrl && (
                  <Box m="6px">
                    <TrackedSecondaryButtonLink
                      size="small"
                      href={secondaryButtonUrl}
                      eventName="CMS Block"
                      eventProperties={{
                        block_position: blockIndex,
                        block_type: 'Call to Action Promo',
                        cta_type: 'Button',
                        button_text: secondaryButtonText,
                        button_number: 2,
                        page_updated: pageUpdated
                      }}
                    >
                      {secondaryButtonText}
                    </TrackedSecondaryButtonLink>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Figcaption>
      <Box flex="1 1 auto" bg="rgba(0, 0, 0, 0.05)">
        {imageDimensions && (
          <Image
            data-qa-id="cta-promo-image"
            image={image}
            alt={headline}
            {...imageDimensions}
          />
        )}
      </Box>
    </Box>
  );
};

export default CtaPromoBlock;
